import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Avatar,
  Grid,
} from '@mui/material';
import { UserContext } from '../../../hooks';
import { toast } from 'react-toastify';
import axiosInstance from '../../../services/axiosInstance';
import {
  getRequiredUserDataMessage,
  MAX_DNI_PHONE_LENGTH,
  MAX_SURNAME_LENGTH,
  normalizeDni,
  normalizePhone,
} from '../../../utils/validation';

const UserProfile = () => {
  const { user, updateUser } = useContext(UserContext);
  const [formData, setFormData] = useState({
    name: user.name || '',
    surname: user.surname || '',
    email: user.email || '',
    dob: user.dob || '',
    image: user.image || '',
    password: '',
    isUca: user.role === 'uca' || false,
    role: user.role || '',
    dni: user.dni || '',
    phone: user.phone || '',
    universityCard: user.universityCard || '',
  });
  const [imagePreview, setImagePreview] = useState(user.image);
  const [imageFile, setImageFile] = useState(null);
  // const [setUniversityCardPreview] = useState(null);

  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      name: user.name,
      surname: user.surname || '',
      email: user.email,
      dob: user.dob ? user.dob.split('T')[0] : '',
      image: user.image,
      universityCard: user.universityCard,
      isUca: user.role === 'uca' || false,
      role: user.role || '',
      dni: user.dni || '',
      phone: user.phone || '',
    }));
    setImagePreview(user.image);
    // setUniversityCardPreview(user.universityCard);
  }, [user]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // const handleCheckboxChange = e => {
  //   setFormData(prev => ({ ...prev, isUca: e.target.checked }));
  // };

  const handleImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  // const handleUniversityCardChange = e => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setUniversityCardPreview(reader.result);
  //     };
  //     reader.readAsDataURL(file);
  //     setFormData(prev => ({ ...prev, universityCard: file }));
  //   }
  // };

  const handleUpdateProfile = async e => {
    e.preventDefault();
    const requiredDataMessage = getRequiredUserDataMessage(formData);
    if (requiredDataMessage) {
      toast.error(
        requiredDataMessage.replace('Antes de reservar', 'Para guardar')
      );
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('surname', formData.surname.trim());
      formDataToSend.append('email', formData.email.trim().toLowerCase());
      formDataToSend.append('dob', formData.dob);
      formDataToSend.append('isUca', formData.isUca);
      formDataToSend.append('dni', normalizeDni(formData.dni));
      formDataToSend.append('phone', normalizePhone(formData.phone));
      if (formData.password) {
        formDataToSend.append('password', formData.password);
      }
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }
      if (formData.universityCard) {
        formDataToSend.append('universityCard', formData.universityCard);
      }

      const response = await axiosInstance.put(
        '/users/update',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.success) {
        updateUser('email', response.user.email);
        updateUser('name', response.user.name);
        updateUser('surname', response.user.surname);
        updateUser('dob', response.user.dob);
        updateUser('role', response.user.role);
        updateUser('image', response.user.image);
        updateUser('universityCard', response.user.universityCard);
        updateUser('isUca', response.user.isUca);
        updateUser('dni', response.user.dni);
        updateUser('phone', response.user.phone);
        toast.success('Perfil actualizado con éxito');
      } else {
        toast.error(response.message || 'Error al actualizar el perfil');
      }
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);
      toast.error(
        error.response?.data?.message ||
          'Error inesperado al actualizar el perfil'
      );
    }
  };

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Detalles de usuario
        </Typography>
        <Box component="form" onSubmit={handleUpdateProfile} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nombre"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Apellidos"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
                inputProps={{ maxLength: MAX_SURNAME_LENGTH }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Correo Electrónico"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Fecha de Nacimiento"
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="DNI"
                name="dni"
                value={formData.dni}
                onChange={handleInputChange}
                inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
                fullWidth
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Teléfono"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
                fullWidth
                variant="outlined"
                required
              />
            </Grid>
            {/* Sección de membresía UCA temporalmente deshabilitada.
            {!isMember && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isUca}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label="Soy universitario de la UCA"
                  />
                </Grid>
                {formData.isUca && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        label="DNI"
                        name="dni"
                        value={formData.dni}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
@@ -206,50 +207,51 @@ const UserProfile = () => {
                            universityCardPreview ||
                            '/default-university-card.png'
                          }
                          alt="University Card"
                          sx={{
                            width: 100,
                            height: 100,
                            cursor: 'pointer',
                            mx: 'auto',
                          }}
                        />
                        <Typography
                          textAlign="center"
                          sx={{ cursor: 'pointer' }}
                        >
                          {universityCardPreview
                            ? 'Cambiar Tarjeta Universitaria'
                            : 'Subir Tarjeta Universitaria'}
                        </Typography>
                      </label>
                    </Grid>
                  </>
                )}
              </>
            )}
            */}
            <Grid item xs={12}>
              <TextField
                label="Contraseña (opcional)"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <input
                type="file"
                accept="image/*"
                id="image-upload"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              <label htmlFor="image-upload">
                <Avatar
                  src={imagePreview || '/default-profile.png'}
                  alt="Profile"
                  sx={{
                    width: 100,
                    height: 100,
                    cursor: 'pointer',
                    mx: 'auto',
                  }}
                />
              </label>
            </Grid>
          </Grid>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
          >
            Guardar Cambios
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserProfile;
